export type ApiFieldError = {
  path: string
  message: string
}

export type FieldErrorSetter<FieldPath extends string = string> = (
  field: FieldPath,
  error: { type: 'server'; message: string },
) => void

type UnknownIssue = {
  path?: unknown
  field?: unknown
  fields?: unknown
  message?: unknown
}

const fieldMessageRules: Array<[RegExp, string]> = [
  [/^ttlSeconds$/, 'Informe um número inteiro entre 0 e 3600 segundos.'],
  [/email/i, 'Confira o e-mail informado.'],
  [/password|senha/i, 'Confira a senha informada.'],
  [/phone|telefone/i, 'Confira o telefone informado.'],
  [/postalcode|zipcode|cep/i, 'Confira o CEP informado.'],
  [/document|cpf|cnpj/i, 'Confira o documento informado.'],
  [/url|link/i, 'Confira o endereço informado.'],
  [/price|amount|currency|valor/i, 'Confira o valor informado.'],
  [/pixel|tracking|conversion|token|secret/i, 'Confira a configuração de tracking.'],
  [/name|nome/i, 'Informe um nome válido.'],
  [/address|endereco|endereço/i, 'Confira o endereço informado.'],
]

export function getSafeApiFieldErrorMessage(path: string): string {
  const rule = fieldMessageRules.find(([pattern]) => pattern.test(path))
  return rule?.[1] ?? 'Revise este campo.'
}

function parseJsonMessage(message: string): unknown {
  try {
    return JSON.parse(message)
  } catch {
    return null
  }
}

function pathToString(path: unknown): string {
  if (Array.isArray(path)) {
    return path.map((part) => String(part)).join('.')
  }

  return typeof path === 'string' ? path : ''
}

function issueToFieldError(issue: unknown): ApiFieldError | null {
  if (!issue || typeof issue !== 'object') return null

  const candidate = issue as UnknownIssue
  const path =
    pathToString(candidate.path) ||
    pathToString(candidate.field) ||
    (Array.isArray(candidate.fields) ? pathToString(candidate.fields[0]) : '')
  const message = typeof candidate.message === 'string' ? candidate.message : ''

  if (!path || !message) return null

  return { path, message: getSafeApiFieldErrorMessage(path) }
}

function issuesFromPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload

  if (!payload || typeof payload !== 'object') return []

  if ('issues' in payload && Array.isArray(payload.issues)) {
    return payload.issues
  }

  if ('fieldErrors' in payload && Array.isArray(payload.fieldErrors)) {
    return payload.fieldErrors
  }

  if ('data' in payload) {
    const nested = (payload as { data?: unknown }).data
    if (nested && typeof nested === 'object' && 'fieldErrors' in nested) {
      const fieldErrors = (nested as { fieldErrors?: unknown }).fieldErrors
      return Array.isArray(fieldErrors) ? fieldErrors : []
    }
  }

  return []
}

export function extractApiFieldErrors(error: unknown): ApiFieldError[] {
  if (!(error instanceof Error)) return []

  const errorData = (error as { data?: unknown }).data
  const dataIssues = issuesFromPayload(errorData)
  if (dataIssues.length > 0) {
    return dataIssues
      .map((issue) => issueToFieldError(issue))
      .filter((issue): issue is ApiFieldError => issue !== null)
  }

  const parsed = parseJsonMessage(error.message)
  const issues = issuesFromPayload(parsed)

  return issues
    .map((issue) => issueToFieldError(issue))
    .filter((issue): issue is ApiFieldError => issue !== null)
}

export function applyApiFieldErrors<FieldPath extends string>(
  error: unknown,
  setError: FieldErrorSetter<FieldPath>,
  fieldMap: Partial<Record<string, FieldPath>> = {},
) {
  const fieldErrors = extractApiFieldErrors(error)

  for (const fieldError of fieldErrors) {
    const field = fieldMap[fieldError.path] ?? (fieldError.path as FieldPath)
    setError(field, { type: 'server', message: fieldError.message })
  }

  return fieldErrors.length > 0
}
